/**
 * Shared primitives for /deliver and /deliver-box:
 *   - DeliveryTabs       — tab strip at the top of each page (cross-link)
 *   - ServerPicker       — radio-style or compact select picker for the server
 *   - ConfirmDeliveryModal — confirm dialog before any RCON write
 *   - passiveCostByTier / tierLabel / tierColor — kept in sync with backend constants
 */
import { Link, useLocation } from "react-router-dom";
import { Wand2, Box, X } from "lucide-react";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";

const COSTS = { 4: 2500, 3: 1750, 2: 1000, 1: 500, 0: 250, "-1": 250, "-2": 250, "-3": 250 };

export function passiveCostByTier(tier) {
  return COSTS[String(tier)] ?? COSTS[tier] ?? 250;
}

export function tierLabel(tier) {
  if (tier >= 4) return "Legendary";
  if (tier === 3) return "Tier 3";
  if (tier === 2) return "Tier 2";
  if (tier === 1) return "Tier 1";
  if (tier === 0) return "Neutral";
  return "Negative";
}

export function tierColor(tier) {
  if (tier >= 4) return "text-amber-300";
  if (tier === 3) return "text-fuchsia-300";
  if (tier === 2) return "text-teal-300";
  if (tier === 1) return "text-sky-300";
  if (tier === 0) return "text-[var(--bone)]";
  return "text-red-300";
}

const TABS = [
  { id: "build", to: "/deliver",     label: "Build a Pal",  icon: Wand2 },
  { id: "box",   to: "/deliver-box", label: "Send From Box", icon: Box },
];

export function DeliveryTabs({ active }) {
  const loc = useLocation();
  return (
    <div className="flex gap-1 mb-6 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="delivery-tabs">
      {TABS.map((t) => {
        const Icon = t.icon;
        const isActive = active ? t.id === active : loc.pathname === t.to;
        return (
          <Link
            key={t.id}
            to={t.to}
            data-testid={`delivery-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-md whitespace-nowrap transition-colors ${
              isActive
                ? "bg-[var(--teal-soft)] text-[var(--teal)] border border-[var(--teal)]"
                : "border border-white/5 hover:border-[var(--teal-soft)]"
            }`}
          >
            <Icon size={16} />
            <span className="text-sm">{t.label}</span>
          </Link>
        );
      })}
    </div>
  );
}

export function ServerPicker({ servers, value, onChange, compact = false }) {
  if (compact) {
    return (
      <select
        className="input w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="deliver-server-select"
      >
        <option value="">Select server…</option>
        {(servers || []).map((s) => (
          <option key={s.name} value={s.name}>{s.name}</option>
        ))}
      </select>
    );
  }
  return (
    <section className="glass-card p-5" data-testid="deliver-server-section">
      <h3 className="text-lg mb-3">Target Server</h3>
      <div className="grid sm:grid-cols-2 gap-2">
        {(servers || []).map((s) => {
          const active = value === s.name;
          return (
            <button
              key={s.name}
              type="button"
              onClick={() => onChange(s.name)}
              data-testid={`deliver-server-${s.name}`}
              className={`p-3 rounded-md border text-left transition-colors ${
                active
                  ? "border-[var(--teal)] bg-[rgba(0,229,255,0.07)] text-[var(--teal)]"
                  : "border-white/10 hover:border-[var(--teal-soft)]"
              }`}
            >
              <div className="text-sm font-semibold">{s.name}</div>
              {s.description && (
                <div className="text-[10px] text-[var(--muted)] mt-0.5">{s.description}</div>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function ConfirmDeliveryModal({
  title, line1, line2,
  confirmLabel = "Confirm & Deliver",
  confirming = false, danger = false,
  onConfirm, onCancel,
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
      onClick={onCancel}
      data-testid="deliver-confirm-modal"
    >
      <div
        className="glass-card max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onCancel}
          aria-label="Close"
          className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--bone)]"
          data-testid="deliver-confirm-close"
        >
          <X size={18} />
        </button>
        <h3 className="text-xl glow-teal mb-3" style={{ fontFamily: "'Cinzel Decorative', serif" }}>{title}</h3>
        <p className="text-sm mb-1">{line1}</p>
        {line2 && (
          <p className="text-xs text-[var(--muted)] mb-4 flex items-center gap-1">
            <ChilletCoinIcon size={12} /> {line2}
          </p>
        )}
        <p className="text-[11px] text-amber-300/80 mb-5 leading-relaxed">
          Coins are deducted only after a successful in-game spawn. If RCON fails, your balance is untouched.
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={confirming}
            className="btn btn-ghost"
            data-testid="deliver-confirm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={confirming}
            className={`btn ${danger ? "btn-danger" : "btn-primary"} disabled:opacity-50`}
            data-testid="deliver-confirm-submit"
          >
            {confirming ? "Delivering…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
